import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  TableRow,
  TableCell,
  Tooltip,
  IconButton,
} from "@mui/material";
import EvidenceDownloadButton from "components/ReusableEvidenceDownload";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import CommentButton from "components/CommentButton";
import DescriptionPopover from "components/DescriptionPopover";
import { AttachFile, Clear, TrendingDown, TrendingFlat, TrendingUp } from "@mui/icons-material";
import { useAuth } from "context/authContext";

const KpiTableRow = React.memo(function KpiTableRow({
  item,
  keyValue,
  perspective,
  view,
  isPrimary,
  targets,
  achievements,
  compactInputStyle,
  handleTargetChange,
  handleAchievementChange,
  handleUpdateKpi,
  handleSaveComment,
  handleConfOpen,
  handleFileAttach,
  handleOpenAssessments,
  getStatusColor,
  getTrendIcon,
  t,
}) {
  const { employee: me } = useAuth();

  return (
    <TableRow key={keyValue} hover sx={{ bgcolor: "white" }}>
      <TableCell>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                lineHeight: 1.2,
                color: "grey.900",
              }}
            >
              {view === "byEmployee" ? item.kpiName : item.employeeName}
            </Typography>
            {/* Optional: Add Perspective name here in tiny text if useful */}
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
              {view === "byEmployee" ? t(`user.assess.perspectives.${perspective}`) : item.jobName}
            </Typography>
          </Box>

          {/* 🔹 NEW DESCRIPTION POPOVER */}
          <DescriptionPopover
            title={view === "byEmployee" ? item.kpiName : item.employeeName}
            description={item.description}
          />
        </Box>
      </TableCell>

      <TableCell>
        <Typography
          variant="caption"
          display="block"
          color="textSecondary"
          sx={{ fontStyle: "italic" }}
        >
          {item.kpiFormula === "Regular"
            ? t("user.myScorecard.regular")
            : t("user.myScorecard.reverse")}{" "}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 500 }}>
          {item.accountability === "Prime"
            ? t("user.myScorecard.prime")
            : t("user.myScorecard.shared")}
        </Typography>
      </TableCell>

      <TableCell align="center">{item.weight}%</TableCell>

      <TableCell align="center">
        {isPrimary ? (
          <TextField
            variant="standard"
            type="number"
            value={targets[keyValue]}
            onChange={(e) => handleTargetChange(keyValue, e.target.value)}
            onBlur={() => handleUpdateKpi(keyValue, "target")}
            InputProps={{
              disableUnderline: true,
              sx: { fontSize: "0.85rem", textAlign: "center" },
            }}
            sx={compactInputStyle}
          />
        ) : (
          <Typography variant="body2">{item.kpiTarget}</Typography>
        )}
      </TableCell>

      <TableCell align="center">
        <Typography variant="caption" display="block" color="textSecondary">
          S: {item.selfAchievement || "-"}
        </Typography>
        {isPrimary && (
          <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
            {t("user.assess.second")} {item.secondaryAchievement || "-"}
          </Typography>
        )}
      </TableCell>

      <TableCell align="center">
        <Box>
          <TextField
            size="small"
            type="number"
            value={achievements[keyValue] ?? ""}
            disabled={item.source?.toLowerCase() === "automated"}
            onChange={(e) => handleAchievementChange(keyValue, e.target.value)}
            onBlur={() => handleUpdateKpi(keyValue, "achievement")}
            sx={{
              ...compactInputStyle,
              "& .MuiOutlinedInput-root": {
                height: 30,
                borderRadius: 1,
                borderColor: achievements[keyValue] ? "success.main" : "grey.300",
              },
            }}
          />

          {item.source?.toLowerCase() === "automated" && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.5,
                color: "text.secondary",
              }}
            >
              Automated
            </Typography>
          )}
        </Box>
      </TableCell>

      <TableCell align="center">
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {item.averageResult?.toFixed(1)}%
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            mt: 0.5,
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              bgcolor: getStatusColor(item.averageResult),
            }}
          />
          {getTrendIcon(item.trend, item.kpiAchievement)}
        </Box>
      </TableCell>

      {/* 🔹 ACTIONS: BOTH EVIDENCES AND COMMENTS */}
      <TableCell align="center">
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {/* Self Evidence */}
          <Tooltip title={t("user.assess.employeeEvidence")}>
            <span>
              <EvidenceDownloadButton
                fileName={item.evidence}
                performanceId={item.performanceId}
                isSelfEvidence={true}
                myScorecardView={false}
              />
            </span>
          </Tooltip>

          {/* Comment Button */}
          <CommentButton
            item={item}
            isPrimary={isPrimary}
            onSave={(val) => handleSaveComment(keyValue, val)}
          />

          {/* Assessor Evidence Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              ml: 0.5,
              borderLeft: "1px solid #eee",
              pl: 0.5,
            }}
          >
            {item.assessorEvidence ? (
              <EvidenceDownloadButton
                fileName={item.assessorEvidence}
                performanceId={item.performanceId}
                isSelfEvidence={false}
                myScorecardView={false}
              />
            ) : (
              <Tooltip title={t("user.assess.assessorEvidence")} arrow>
                <span>
                  <EvidenceDownloadButton
                    fileName={null}
                    performanceId={item.performanceId}
                    isSelfEvidence={false}
                    myScorecardView={false}
                  />
                </span>
              </Tooltip>
            )}

            {isPrimary && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {/* 🔹 DELETE BUTTON: Only show if file exists */}
                {item.assessorEvidence && (
                  <Tooltip title={t("user.assess.removeDocument")}>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleConfOpen(item, false)}
                    >
                      <Clear fontSize="small" sx={{ fontSize: "1rem" }} />
                    </IconButton>
                  </Tooltip>
                )}

                {/* UPLOAD BUTTON */}
                <input
                  type="file"
                  id={`file-upload-${keyValue}`}
                  hidden
                  onChange={(e) => handleFileAttach(e, item, false)}
                />
                <label htmlFor={`file-upload-${keyValue}`}>
                  <Tooltip
                    title={
                      item.assessorEvidence
                        ? t("user.assess.overrideDocument")
                        : t("user.assess.attachDocument")
                    }
                  >
                    <IconButton component="span" size="small">
                      <AttachFile
                        fontSize="small"
                        sx={{
                          color: item.assessorEvidence ? "success.main" : "inherit",
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </label>
              </Box>
            )}
          </Box>
          <CommentButton
            item={item}
            isPrimary={true}
            isAdminScorecardView={true}
            currentUser={me}
            // Using the specific name from your new backend logic
            allComments={item.adminPerformanceComments || []}
            onSave={(newComment) => {}}
            userAssessView={true}
            // Disable only if there is no performanceId yet (not assessed)
            disabled={!item.performanceId}
          />
        </Box>
      </TableCell>
      <TableCell align="center">
        <IconButton size="small" onClick={(e) => handleOpenAssessments(e, item)}>
          <KeyboardArrowDown fontSize="small" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
});

KpiTableRow.propTypes = {
  item: PropTypes.shape({
    kpiName: PropTypes.string,
    employeeName: PropTypes.string,
    description: PropTypes.string,
    kpiFormula: PropTypes.string,
    accountability: PropTypes.string,
    weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    kpiTarget: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    selfAchievement: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    secondaryAchievement: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    averageResult: PropTypes.number,
    trend: PropTypes.string,
    kpiAchievement: PropTypes.any,
    evidence: PropTypes.string,
    assessorEvidence: PropTypes.string,
    performanceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    adminPerformanceComments: PropTypes.array,
    source: PropTypes.string,
    jobName: PropTypes.string,
  }).isRequired,

  keyValue: PropTypes.string.isRequired,

  perspective: PropTypes.string,

  view: PropTypes.oneOf(["byEmployee", "byKpi"]),

  isPrimary: PropTypes.bool,

  targets: PropTypes.object,
  achievements: PropTypes.object,

  compactInputStyle: PropTypes.object,

  handleTargetChange: PropTypes.func.isRequired,
  handleAchievementChange: PropTypes.func.isRequired,
  handleUpdateKpi: PropTypes.func.isRequired,
  handleSaveComment: PropTypes.func.isRequired,
  handleConfOpen: PropTypes.func.isRequired,
  handleFileAttach: PropTypes.func.isRequired,
  handleOpenAssessments: PropTypes.func.isRequired,

  getStatusColor: PropTypes.func.isRequired,
  getTrendIcon: PropTypes.func.isRequired,

  t: PropTypes.func.isRequired,
};

export default KpiTableRow;
